/**
 * Feature-facing presence hooks.
 *
 * - `useRecordPresence` — for a record's detail view. Marks the current user as actively
 *   viewing the record (a `view` hold, broadcast to others) and returns the live roster.
 * - `useObservedRecordPresence` — for list rows. Observes a record's roster (a `watch` hold,
 *   never counted as present) and returns it for display.
 *
 * Both return viewers ordered others-first with the current user's own tokens flagged and
 * pushed to the end, mirroring collaborative-presence convention (Figma / Linear). The roster
 * comes from the shell-mounted channel via the external store; these hooks only acquire/release
 * the hold and read their slice.
 */

import { useEffect, useMemo } from "react";
import { useSyncExternalStore } from "react";

import { useCurrentUser } from "../../identity";
import { presenceParticipation } from "./presenceParticipation";
import { presenceStore } from "./presenceStore";
import {
    PRESENCE_ENABLED,
    type PresenceEntityType,
    type PresenceHold,
    type PresenceMember,
} from "./presenceWire";

export interface PresenceViewer extends PresenceMember {
    /** True when this token belongs to the current user (a different tab / device counts too). */
    readonly isSelf: boolean;
}

const EMPTY_MEMBERS: readonly PresenceMember[] = Object.freeze([]);
const EMPTY_VIEWERS: readonly PresenceViewer[] = Object.freeze([]);

function usePresenceHold(
    entityType: PresenceEntityType,
    entityId: string | undefined,
    hold: PresenceHold,
): void {
    const enabled = PRESENCE_ENABLED && typeof entityId === "string" && entityId.length > 0;

    useEffect(() => {
        if (!enabled || entityId === undefined) {
            return;
        }
        const ref = { entityType, entityId } as const;
        presenceParticipation.acquire(ref, hold);
        return () => {
            presenceParticipation.release(ref, hold);
        };
    }, [enabled, entityType, entityId, hold]);
}

function usePresenceRoster(
    entityType: PresenceEntityType,
    entityId: string | undefined,
): readonly PresenceViewer[] {
    const currentUserId = useCurrentUser().data?.userId ?? null;

    const members = useSyncExternalStore(presenceStore.subscribe, () =>
        entityId !== undefined && entityId.length > 0
            ? presenceStore.getRoom(entityType, entityId)
            : EMPTY_MEMBERS,
    );

    return useMemo(() => orderViewers(members, currentUserId), [members, currentUserId]);
}

function orderViewers(
    members: readonly PresenceMember[],
    currentUserId: string | null,
): readonly PresenceViewer[] {
    if (members.length === 0) {
        return EMPTY_VIEWERS;
    }
    const others: PresenceViewer[] = [];
    const own: PresenceViewer[] = [];
    for (const member of members) {
        const viewer: PresenceViewer = {
            ...member,
            isSelf: currentUserId !== null && member.galileoUserId === currentUserId,
        };
        (viewer.isSelf ? own : others).push(viewer);
    }
    return [...others, ...own];
}

/** Detail-view presence: count the current user as present and return the live roster. */
export function useRecordPresence(
    entityType: PresenceEntityType,
    entityId: string | undefined,
): readonly PresenceViewer[] {
    usePresenceHold(entityType, entityId, "view");
    return usePresenceRoster(entityType, entityId);
}

/** List-row presence: observe a record's roster without being counted as present. */
export function useObservedRecordPresence(
    entityType: PresenceEntityType,
    entityId: string | undefined,
): readonly PresenceViewer[] {
    usePresenceHold(entityType, entityId, "watch");
    return usePresenceRoster(entityType, entityId);
}
